import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEventData, useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { OperatoryAvailabilityBanner } from '@frontend/schedule-operatory-availability-banner';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { CalendarLocationSelectorModal } from '../../components/calendar-location-selector-modal';
import { ColumnEntityReorderActions } from '../../components/calendar-view/calendar-column-entity-list-section/column-entity-reorder-actions';
import { CalendarView } from '../../components/calendar-view/calendar-view';
import { CalendarViewMainV3 } from '../../components/calendar-view/calendar-view-v3/calendar-view-main';
import { useColumnEntityReorderStore } from '../../components/calendar-view/providers';
import { CalendarViewOffices, OfficeCalendarView } from '../../components/calendar-view/types';
import { ZoomProvider } from '../../components/calendar-view/zoom/zoom-context';
import { ZoomControls } from '../../components/calendar-view/zoom/zoom-controls';
import { EventCardComponent } from '../../components/schedule-calendar-components/event-card';
import { hasCalendarScheduleWorkingHoursSet } from '../../hooks/scheduler-v3/calendar-schedule-definitions';
import { useGetCalendarColumnEntityData } from '../../hooks/scheduler-v3/use-get-calendar-column-entity-data';
import { useGetCalendarScheduleV3Data } from '../../hooks/scheduler-v3/use-get-calendar-schedule-v3-data';
import { useGetCalendarV3QueriesData } from '../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useGetOfficeHoursDetailsForCalendarV3 } from '../../hooks/scheduler-v3/use-get-office-hours-details-for-calendar-v3';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useCalendarStore } from '../../stores/calendar.store';
import { useScheduleActionsContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-actions-context-value';
import { useScheduleRequestModalContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-request-modal-context-value';
import { ScheduleRequestFormManager } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestFormManager';
import { ScheduleRequestPanel } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/ScheduleRequestWrappers/ScheduleRequestPanel';
import { CalendarEventType } from '../Calendar/types';
import { isUUID } from '../Calendar/utils';
import { CalendarAutoScroll } from './components/calendar-auto-scroll';
import { CalendarHeader } from './components/header';
import { OnboardingFlowEmptyCalendarComponent } from './onboarding-flow/onboarding-flow-empty-calendar-component';

export const CalendarV3Container = () => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { getScopeName, selectedLocationIds, getLocationTimeZone } = useSchedulingLocationInfo();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();

  const {
    calendarEventsWithConvertedTimes,
    providersData,
    operatoryData,
    calendarLocationIds,
    calendarCounts,
    calendarTodayDate,
    isLoadingCalendarEvents,
    scheduledRequestsWithConvertedTimes,
    refetchScheduleRequestList,
  } = useGetCalendarV3QueriesData();
  const { officeSchedulesQuery, providerSchedulesQuery } = useGetCalendarScheduleV3Data(
    calendarLocationIds.map((id) => ({
      locationId: id,
      providerIds: providersData?.providers.filter((p) => p.locationId === id).map((p) => p.id),
      operatoryIds: operatoryData?.operatories.filter((o) => o.locationId === id).map((o) => o.id),
    }))
  );

  const { calendarDate, calendarProviderIds, calendarOperatories } = useCalendarStore([
    'calendarDate',
    'calendarProviderIds',
    'calendarOperatories',
  ]);

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();

  const { isIntegratedOffice, isLoading: isLoadingIntegrations } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isV3OperatorySupported, isLoading: isLoadingV3OperatorySupported } =
    SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { calendarEntityType, setCalendarEntityType } = useCalendarStore([
    'calendarEntityType',
    'setCalendarEntityType',
  ]);

  const { hasAiScheduleAccess } = useAiScheduleAccess();

  const showOperatoryAvailabilityBanner =
    calendarEntityType === 'operatory' && isV3OperatorySupported && !hasAiScheduleAccess;

  const {
    selectedScheduleRequest: selectedScheduleRequestEventData,
    setSelectedScheduleRequest: setSelectedScheduleRequestEventData,
    scheduleRequestModalContextValue,
  } = useScheduleRequestModalContextValue<ScheduleRequestEventData>({ refetchScheduleRequestList });
  const scheduleActionsContextValue = useScheduleActionsContextValue();

  // Reset calendar entity type to 'provider' for offices which do not have operatory calendar view enabled
  useEffect(() => {
    if (isLoadingV3OperatorySupported) return;

    if (!isV3OperatorySupported && calendarEntityType === 'operatory') {
      setCalendarEntityType('provider');
    }
  }, [
    calendarEntityType,
    isIntegratedOffice,
    isLoadingIntegrations,
    isLoadingV3OperatorySupported,
    isV3OperatorySupported,
    setCalendarEntityType,
  ]);

  // Handle error while fetching provider schedules
  useEffect(() => {
    if (providerSchedulesQuery.isError) {
      alert.error(t('Error fetching provider schedules'));
    }
  }, [alert, providerSchedulesQuery.isError, t]);

  // Handle error while fetching office schedules
  useEffect(() => {
    if (officeSchedulesQuery.isError) {
      alert.error(t('Error fetching office schedules'));
    }
  }, [alert, officeSchedulesQuery.isError, t]);

  const sortedOperatories = useMemo(() => {
    const operatories = calendarOperatories.length
      ? operatoryData?.operatories?.filter(({ id }) => calendarOperatories.includes(id))
      : operatoryData?.operatories ?? [];
    return (
      operatories?.sort((a, b) =>
        ((a.displayName || a.externalName) ?? '').localeCompare((b.displayName || b.externalName) ?? '')
      ) ?? []
    );
  }, [calendarOperatories, operatoryData?.operatories]);

  const sortedProviders = useMemo(() => {
    const providers =
      (calendarProviderIds.length
        ? providersData?.providers.filter((p) => calendarProviderIds.includes(p.id))
        : providersData?.providers) ?? [];
    return providers
      .map((p) => ({ ...p, fullName: getFullName(p) }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [calendarProviderIds, providersData?.providers]);

  const { getCalendarColumnEntityData } = useGetCalendarColumnEntityData({
    calendarEvents: calendarEventsWithConvertedTimes,
    scheduleRequests: scheduledRequestsWithConvertedTimes,
    providers: sortedProviders,
    operatories: sortedOperatories,
    providerSchedules: providerSchedulesQuery.data,
  });

  const { getOfficeExceptionEventsForCalendarObject, getOfficeWorkingHours, getOfficeAvailabilityExceptions } =
    useGetOfficeHoursDetailsForCalendarV3({ schedules: officeSchedulesQuery.data, isScheduleDefinitionsEnabled });

  // TODO: Review this logic to handle the configured calendar event
  const handleConfiguredCalendarEvent = (configuredCalendarEvent: CalendarEventType) => {
    const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
    const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
    if (isOfficeEvent || isProviderEvent) {
      setConfiguredCalendarEvent(configuredCalendarEvent);
    }
  };

  const getOfficeCalendarViewObject = useCallback(
    (locationId: string): OfficeCalendarView => {
      const { availabilityExceptions, isAllDayOff } = getOfficeAvailabilityExceptions(locationId);
      return {
        officeName: getScopeName(locationId),
        officeTimezone: getLocationTimeZone(locationId),
        officeExceptions: isScheduleDefinitionsEnabled ? [] : getOfficeExceptionEventsForCalendarObject(locationId),
        hasAnyWorkingHoursSet: isScheduleDefinitionsEnabled
          ? false
          : hasCalendarScheduleWorkingHoursSet(
              officeSchedulesQuery.data.find((s) => s.locationId === locationId && s.id === locationId)
            ),
        workingHours: getOfficeWorkingHours(locationId),
        availabilityExceptions,
        isAllDayOff,
        columnEntities: getCalendarColumnEntityData(locationId),
        locationId: locationId,
        calendarDateValue: formatDate(calendarDate, 'YYYY-MM-DD'),
      };
    },
    [
      getScopeName,
      getLocationTimeZone,
      getOfficeExceptionEventsForCalendarObject,
      officeSchedulesQuery.data,
      isScheduleDefinitionsEnabled,
      getOfficeWorkingHours,
      getOfficeAvailabilityExceptions,
      getCalendarColumnEntityData,
      calendarDate,
    ]
  );

  const calendarObject = useMemo<CalendarViewOffices>(() => {
    if (calendarLocationIds.length)
      return calendarLocationIds.map((locationId) => getOfficeCalendarViewObject(locationId ?? ''));

    return [getOfficeCalendarViewObject(selectedLocationIds[0])];
  }, [calendarLocationIds, selectedLocationIds, getOfficeCalendarViewObject]);

  return (
    <>
      <section css={containerStyles} id='calendar-view-wrapper'>
        {showOperatoryAvailabilityBanner && <OperatoryAvailabilityBanner />}
        {isInReorderMode ? (
          <ColumnEntityReorderActions />
        ) : (
          <CalendarHeader
            calendarEventsCount={calendarCounts.total}
            insuranceAlertCalendarEventsCount={calendarCounts.insurance}
            unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
            calendarTodayDate={calendarTodayDate}
          />
        )}
        <div css={{ position: 'relative', overflow: 'auto' }}>
          <ContentLoader
            show={isLoadingCalendarEvents}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          <CalendarView
            data={calendarObject}
            onEventCreate={(startHour, endHour, providerName, providerId, isValid, locationId, calendarDateValue) => {
              handleConfiguredCalendarEvent({
                startHour,
                endHour,
                providerName,
                providerId,
                isValid,
                locationId,
                calendarDateValue,
              });
            }}
            EventCardComponent={EventCardComponent}
            shouldAllowEventCreation={hasWSM}
            EmptyCalendarComponent={isLoadingCalendarEvents ? undefined : OnboardingFlowEmptyCalendarComponent}
            calendarDate={calendarDate}
          >
            <ZoomProvider>
              <CalendarAutoScroll calendarObject={calendarObject} isLoadingCalendarEvents={isLoadingCalendarEvents} />
              <CalendarViewMainV3
                isScheduleDefinitionsEnabled={isScheduleDefinitionsEnabled}
                selectedScheduleRequestEventData={selectedScheduleRequestEventData}
                setSelectedScheduleRequestEventData={(request) => {
                  setSelectedScheduleRequestEventData(request);
                  scheduleRequestModalContextValue.scheduleRequestModalProps.openModal();
                }}
              />
              <ZoomControls />
            </ZoomProvider>
          </CalendarView>
        </div>
      </section>

      <CalendarLocationSelectorModal />
      {selectedScheduleRequestEventData && (
        <ScheduleRequestFormManager
          wrapper={ScheduleRequestPanel}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequestEventData}
          scheduleActionsContextValue={scheduleActionsContextValue}
        />
      )}
    </>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  containerType: 'inline-size',
});
